import React, { useEffect, useState, useContext } from "react";

import classNames from "classnames";
import isEmpty from "lodash.isempty";
import { observer } from "mobx-react";
import dynamic from "next/dynamic";
import { useSearchParams, useParams } from "../../../hooks/useNavigation";
import { lighten } from "polished";
import { isMobile } from "react-device-detect";
import styled from "styled-components";

import ComponentContext from "../../../../contexts/ComponentContext";
import { useStores } from "../../../../contexts/StoreContext";
import breakpoints from "../../../constants";
import {
  useAnalytics,
  getUTMFromParams,
} from "../../../helpers/AnalyticsTrackingContext";
import { manageSessionId } from "../../../helpers/tracking";
import { hoursTypeMap } from "../../../helpers/vacancyHelpers";
import Button from "../../Button/Button";
import ApplicationFormModal from "../ApplicationFormModal";
import VacancyImage from "../VacancyImage";
import VacancyLoader from "../VacnacyLoader";
import ReferAFriend from "./ReferSomeone";

const DynamicTFChatBot = dynamic(
  () =>
    import("@e4s-developers/tf-careersite-chatbot").then((mod) => {
      return mod.TFChatBot;
    }),
  {
    ssr: false,
  }
);

const StyledButtonWrapper = styled.div`
  position: relative;
  flex-wrap: wrap;
  @media (min-width: ${breakpoints.DESKTOP}) {
    flex-wrap: nowrap;
  }
`;

const VacancyContainer = styled.div`
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  background-color: ${({ bgColour }) => bgColour || "#fff"};

  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);

  .vacancy-image {
    margin-right: 16px;
  }
`;

const VacancyHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const JobTitle = styled.h3`
  font-weight: bold;
  margin-bottom: 4px;
  color: ${({ titleTextColour }) => titleTextColour || "inherit"};
`;

const StyledButton = styled(Button)`
  color: ${({ buttonTextColour }) => buttonTextColour || "inherit"};
  background-color: ${({ buttonColour }) => buttonColour || "inherit"};
  border: ${({ buttonColour }) =>
    buttonColour ? `1px solid ${lighten(buttonColour)}` : "inherit"};
  width: ${({ buttonWidth }) => buttonWidth || "200px"};

  &:disabled {
    background-color: ${({ buttonColour }) =>
      buttonColour ? lighten(0.3, buttonColour) : "inherit"};
    border: ${({ buttonColour }) =>
      buttonColour ? `1px solid ${lighten(buttonColour)}` : "inherit"};
  }

  &:hover,
  &:active,
  &:focus {
    background-color: ${({ buttonHoverColour }) =>
      buttonHoverColour || "inherit"};
    border: ${({ buttonHoverColour }) =>
      buttonHoverColour ? `1px solid ${buttonHoverColour}` : "inherit"};
  }
`;

const BrandName = styled.p`
  color: #666;
  font-size: 15px;
  margin-bottom: 16px;
`;

const JobDetails = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 12px;
  font-size: 14px;
  color: #444;

  svg {
    margin-right: 4px;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 28px;

  button {
    margin-right: 8px;

    &:last-child {
      margin-right: 0;
    }
  }
`;

const VacancyDescription = styled.div`
  margin-top: 16px;
  font-size: 14px;
  color: ${({ textColour }) => textColour || "#444"};
`;

function VacancyItem() {
  const {
    thumbnail,
    formHeaderBackgroundColor,
    applyButtonText = "Apply",
    applyButtonBackgroundColour = "",
    applyButtonBackgroundHoverColour = "",
    applyButtonTextColour = "",
    descriptionTextColour = "",
    titleTextColour = "",
    descriptionBackgroundColour = "",
    referAFriend,
    applyButtonClassName,
    applyButtonHeight = "3rem",
    applyButtonWidth,
    additionalConfig,
  } = useContext(ComponentContext);

  const { modalStore, vacancyStore, configStore } = useStores();
  const { openModal } = modalStore;
  const { sendTrackingEvent } = useAnalytics();
  const [isVacancyActive, setIsVacancyActive] = useState(true);
  const { selectedVacancy, isVacancyLoading } = vacancyStore;
  const [showInteractiveApp, setShowInteractiveApp] = useState(false);
  const params = useParams();
  const searchParams = useSearchParams();
  const { trackingStore } = useStores();
  const search = Object.fromEntries(searchParams.entries());
  const combindedParms = { ...params, ...search };
  const { refererQueryString } = trackingStore;
  const utm = getUTMFromParams(refererQueryString);

  const { config } = configStore;

  const smartApply = combindedParms["smart-apply"];
  const isInteractiveApply = smartApply === "true";
  const hasInteractiveApplyForm = Boolean(
    selectedVacancy?.applicationDetail?.appId
  );

  const thumbnailUrl =
    thumbnail?.formats?.thumbnail?.url || thumbnail?.url || null;

  const imageUrl = selectedVacancy?.company?.logoUrlFull || thumbnailUrl;

  const buttonClassNames = classNames(
    "px-5 text-nowrap text-uppercase",
    {
      "w-100 my-4": isMobile,
    },
    applyButtonClassName
  );

  useEffect(() => {
    if (!isEmpty(selectedVacancy)) {
      const isVacancyClosed = selectedVacancy?.status === "CLOSED";
      setIsVacancyActive(!isVacancyClosed);
    }
  }, [selectedVacancy]);

  if (isVacancyLoading) {
    return <VacancyLoader />;
  }

  const applyButton = (
    <StyledButton
      className={buttonClassNames}
      maxHeight={applyButtonHeight}
      buttonWidth={applyButtonWidth}
      height={applyButtonHeight}
      buttonTextColour={applyButtonTextColour}
      buttonColour={applyButtonBackgroundColour}
      buttonHoverColour={applyButtonBackgroundHoverColour}
      onClick={() => {
        const jobId = selectedVacancy?.vacancyId || selectedVacancy?.id;
        sendTrackingEvent("APPLY_BUTTON_CLICK", jobId, {
          vacancyId: jobId,
        });

        openModal({
          content: (
            <ApplicationFormModal
              formHeaderBackgroundColor={formHeaderBackgroundColor}
              thumbnailUrl={thumbnailUrl}
            />
          ),
          fullscreen: isMobile,
        });
      }}
    >
      {applyButtonText}
    </StyledButton>
  );

  const referAFriendButton = (
    <StyledButton
      className={buttonClassNames}
      id="refer-a-friend-modal-button"
      maxHeight={applyButtonHeight}
      height={applyButtonHeight}
      buttonTextColour={applyButtonTextColour}
      buttonColour={applyButtonBackgroundColour}
      buttonWidth={applyButtonWidth}
      buttonHoverColour={applyButtonBackgroundHoverColour}
      onClick={() => {
        const jobId = selectedVacancy?.vacancyId || selectedVacancy?.id;
        sendTrackingEvent("REFER_A_FRIEND_BUTTON_CLICK", jobId, {
          vacancyId: jobId,
        });

        openModal({
          content: (
            <ReferAFriend
              text={referAFriend?.text}
              buttonText={referAFriend?.buttonText}
              includeEmailAddress={referAFriend?.includeEmailAddress}
              includeFirstName={referAFriend?.includeFirstName}
              includeLastName={referAFriend?.includeLastName}
              successText={referAFriend?.successText}
              copyButtonText={referAFriend?.copyButtonText}
              customSiteUrl={referAFriend?.customSiteUrl}
            />
          ),
          fullscreen: isMobile,
        });
      }}
    >
      Refer Someone
    </StyledButton>
  );

  const interactiveApplyButton = (
    <StyledButton
      className={buttonClassNames}
      buttonTextColour={applyButtonTextColour}
      buttonColour={applyButtonBackgroundColour}
      buttonHoverColour={applyButtonBackgroundHoverColour}
      buttonWidth={applyButtonWidth}
      id="vacancy-smart-apply-button"
      maxHeight={applyButtonHeight}
      height={applyButtonHeight}
      onClick={() => {
        const jobId = selectedVacancy?.vacancyId || selectedVacancy?.id;
        sendTrackingEvent("SMART_APPLY_BUTTON_CLICK", jobId, {
          vacancyId: jobId,
        });

        setShowInteractiveApp(!showInteractiveApp);
      }}
    >
      <span style={{ alignItems: "center", display: "flex" }}>
        <svg
          className="icon"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          style={{
            height: "24px",
            marginRight: "10px",
            width: "24px",
          }}
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 01-.923 1.785A5.969 5.969 0 006 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337z"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Smart Apply
      </span>
    </StyledButton>
  );

  let openedSince = "";

  if (additionalConfig?.showTitleSinceOpened) {
    // calculate the difference since the job was opened to today eg 2 days ago
    const today = new Date();
    const openedDate = new Date(selectedVacancy?.validFrom);
    const diffTime = Math.abs(today - openedDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    openedSince = `${diffDays}d`;
  }
  const formHeaderTextColor =
    config?.applicationFormConfig?.textColor || "#FFFFFF";
  const backgroundColor = config?.applicationFormConfig?.backgroundColor;

  return (
    <VacancyContainer
      className="overflow-auto"
      bgColour={descriptionBackgroundColour}
    >
      <VacancyHeader>
        <div className="d-flex ">
          {imageUrl ? (
            <div className="vacancy-image">
              <VacancyImage imageUrl={imageUrl} small />
            </div>
          ) : null}
          <div className="d-flex flex-column">
            <JobTitle titleTextColour={titleTextColour}>
              {selectedVacancy?.jobTitle}
            </JobTitle>
            <BrandName>
              <span className="me-2">{selectedVacancy?.company?.name}</span>
              {additionalConfig?.showTitleSinceOpened && openedSince ? (
                <>
                  {"  •  "}
                  <span className="opened-since-label ms-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M14 6.74667H9.48L11.3067 4.86667C9.48666 3.06667 6.54 3 4.72 4.8C2.9 6.60667 2.9 9.52 4.72 11.3267C6.54 13.1333 9.48666 13.1333 11.3067 11.3267C12.2133 10.4333 12.6667 9.38667 12.6667 8.06667H14C14 9.38667 13.4133 11.1 12.24 12.26C9.9 14.58 6.1 14.58 3.76 12.26C1.42666 9.94667 1.40666 6.18667 3.74666 3.87333C6.08666 1.56 9.84 1.56 12.18 3.87333L14 2V6.74667ZM8.33333 5.33333V8.16667L10.6667 9.55333L10.1867 10.36L7.33333 8.66667V5.33333H8.33333Z"
                        fill="#1C1C1C"
                        fillOpacity="0.6"
                      />
                    </svg>{" "}
                    {openedSince}
                  </span>
                </>
              ) : null}
            </BrandName>
          </div>
        </div>
      </VacancyHeader>
      <hr />
      <JobDetails>
        <div className="d-flex">
          <svg
            width="20"
            height="20"
            viewBox="0 0 16 22"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="me-2"
          >
            <path
              d="M7.72612 0.333008C3.46592 0.333008 0 3.79892 0 8.05909C0 13.3461 6.91412 21.1077 7.2085 21.4355C7.485 21.7435 7.96775 21.743 8.24375 21.4355C8.53812 21.1077 15.4522 13.3461 15.4522 8.05909C15.4522 3.79892 11.9863 0.333008 7.72612 0.333008ZM7.72612 11.9463C5.58271 11.9463 3.83896 10.2025 3.83896 8.05909C3.83896 5.91567 5.58275 4.17192 7.72612 4.17192C9.8695 4.17192 11.6132 5.91572 11.6132 8.05913C11.6132 10.2025 9.8695 11.9463 7.72612 11.9463Z"
              fill="#1C1C1C"
            />
          </svg>
          {selectedVacancy?.location?.city}
        </div>
        <div className="d-flex">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            className="me-2"
          >
            <path
              d="M9.7249 3.60001C9.30719 3.60001 8.90658 3.78965 8.61121 4.12721C8.31584 4.46478 8.1499 4.92262 8.1499 5.40001V6.00001H4.4749C4.05719 6.00001 3.65658 6.18965 3.36121 6.52721C3.06584 6.86478 2.8999 7.32262 2.8999 7.80001V9.46081L10.8946 11.8968C11.1602 11.9776 11.4396 11.9776 11.7052 11.8968L19.6999 9.46081V7.80001C19.6999 7.32262 19.534 6.86478 19.2386 6.52721C18.9432 6.18965 18.5426 6.00001 18.1249 6.00001H14.4499V5.40001C14.4499 4.92262 14.284 4.46478 13.9886 4.12721C13.6932 3.78965 13.2926 3.60001 12.8749 3.60001H9.7249ZM9.7249 4.80001H12.8749C13.0141 4.80001 13.1477 4.86322 13.2461 4.97574C13.3446 5.08826 13.3999 5.24088 13.3999 5.40001V6.00001H9.1999V5.40001C9.1999 5.24088 9.25521 5.08826 9.35367 4.97574C9.45213 4.86322 9.58566 4.80001 9.7249 4.80001Z"
              fill="#1C1C1C"
            />
            <path
              d="M2.8999 17.4001C2.8999 17.8775 3.06584 18.3354 3.36121 18.6729C3.65658 19.0105 4.05719 19.2001 4.4749 19.2001H18.1249C18.5426 19.2001 18.9432 19.0105 19.2386 18.6729C19.534 18.3354 19.6999 17.8775 19.6999 17.4001V10.6201L11.4354 13.1365C11.3466 13.1636 11.2532 13.1636 11.1645 13.1365L2.8999 10.6201V17.4001Z"
              fill="#1C1C1C"
            />
          </svg>
          {hoursTypeMap[selectedVacancy?.hoursType]}
        </div>
        <div className="d-flex">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            className="me-2"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.5 2C9.4233 2 7.39323 2.61581 5.66652 3.76957C3.9398 4.92332 2.59399 6.5632 1.79927 8.48182C1.00455 10.4004 0.796614 12.5116 1.20176 14.5484C1.6069 16.5852 2.60693 18.4562 4.07538 19.9246C5.54383 21.3931 7.41475 22.3931 9.45155 22.7982C11.4884 23.2034 13.5996 22.9954 15.5182 22.2007C17.4368 21.406 19.0767 20.0602 20.2304 18.3335C21.3842 16.6068 22 14.5767 22 12.5C22 11.1211 21.7284 9.75574 21.2007 8.48182C20.6731 7.2079 19.8996 6.05039 18.9246 5.07538C17.9496 4.10036 16.7921 3.32694 15.5182 2.79926C14.2443 2.27159 12.8789 2 11.5 2ZM14.3327 17.8102H8.23334C8.08907 17.8102 7.94834 17.7656 7.83039 17.6825C7.71244 17.5995 7.62303 17.482 7.5744 17.3461C7.52578 17.2103 7.5203 17.0628 7.55873 16.9237C7.59715 16.7847 7.6776 16.6609 7.78907 16.5693L10.2232 14.5697C10.0482 14.2985 9.82234 13.949 9.5904 13.5897H8.23334C8.04769 13.5897 7.86964 13.5159 7.73836 13.3846C7.60709 13.2534 7.53334 13.0753 7.53334 12.8897C7.53334 12.704 7.60709 12.526 7.73836 12.3947C7.86964 12.2634 8.04769 12.1897 8.23334 12.1897H8.7C8.602 12.0329 8.51567 11.8943 8.4606 11.8019C8.1773 11.3051 8.02596 10.7441 8.021 10.1723C8.02085 9.44844 8.26328 8.74545 8.70957 8.17559C9.15586 7.60572 9.78026 7.20187 10.483 7.02853C11.1858 6.85519 11.9264 6.92237 12.5864 7.21934C13.2465 7.51631 13.7881 8.02593 14.1245 8.6668C14.2112 8.8311 14.229 9.02309 14.1741 9.20053C14.1192 9.37797 13.996 9.52633 13.8317 9.61296C13.6674 9.6996 13.4754 9.71742 13.298 9.66251C13.1205 9.60759 12.9722 9.48443 12.8855 9.32013C12.6948 8.95619 12.3875 8.6667 12.0128 8.49793C11.6382 8.32917 11.2178 8.29088 10.8188 8.38917C10.4198 8.48747 10.0653 8.71668 9.81189 9.04017C9.5585 9.36366 9.42088 9.76275 9.421 10.1737C9.42557 10.4919 9.50792 10.8041 9.66087 11.0832C9.78547 11.2909 10.0547 11.716 10.3581 12.1911H12.8972C13.0829 12.1911 13.2609 12.2648 13.3922 12.3961C13.5235 12.5274 13.5972 12.7054 13.5972 12.8911C13.5972 13.0767 13.5235 13.2548 13.3922 13.386C13.2609 13.5173 13.0829 13.5911 12.8972 13.5911H11.2578C11.5331 14.0162 11.7338 14.3261 11.7399 14.3349C11.8336 14.4793 11.8709 14.6532 11.8445 14.8233C11.8182 14.9934 11.7301 15.1478 11.5971 15.2571L10.1901 16.413H14.3327C14.5183 16.413 14.6964 16.4867 14.8276 16.618C14.9589 16.7493 15.0327 16.9273 15.0327 17.113C15.0327 17.2986 14.9589 17.4767 14.8276 17.608C14.6964 17.7392 14.5183 17.813 14.3327 17.813V17.8102Z"
              fill="#1C1C1C"
            />
          </svg>
          {selectedVacancy?.remuneration?.description}
        </div>
      </JobDetails>

      <ButtonsContainer>
        {isVacancyActive && applyButton}
        {referAFriend && referAFriendButton}
        {(config?.allowInteractiveApplications || isInteractiveApply) &&
          isVacancyActive &&
          hasInteractiveApplyForm &&
          interactiveApplyButton}
      </ButtonsContainer>

      <VacancyDescription textColour={descriptionTextColour}>
        <div
          className="mb-5"
          dangerouslySetInnerHTML={{ __html: selectedVacancy?.description }}
        />
        <StyledButtonWrapper className="d-flex gap-3">
          {isVacancyActive && applyButton}
          {(config?.allowInteractiveApplications || isInteractiveApply) &&
            isVacancyActive &&
            hasInteractiveApplyForm &&
            interactiveApplyButton}
        </StyledButtonWrapper>
      </VacancyDescription>
      {showInteractiveApp && (
        <DynamicTFChatBot
          formId={selectedVacancy?.applicationDetail?.appId}
          sId={manageSessionId()}
          onClose={() => setShowInteractiveApp(false)}
          show={showInteractiveApp}
          tenant={selectedVacancy?.tenant}
          vacancyTitle={selectedVacancy?.title || selectedVacancy?.jobTitle}
          style={{
            headerBackgroundColour:
              backgroundColor || formHeaderBackgroundColor,
            headerTextColour: formHeaderTextColor,
            headerLogo: selectedVacancy?.company?.logoUrlFull,
          }}
          utm={utm}
        />
      )}
    </VacancyContainer>
  );
}

export default observer(VacancyItem);
